import { ref } from 'vue';
import ReportsAPI from '@/api/reports';
import { showToast } from '@/utilities/toast';
import { filenameFromContentDisposition } from '@/utilities/contentDisposition';

export function useReports() {
  const reports = ref([]);

  // Export-related state
  const emails = ref([]);
  const contactMe = ref(false);
  const exportSuccess = ref(false);

  const fetchReports = async () => {
    const response = await ReportsAPI.getAll();

    if (response) {
      reports.value = (Array.isArray(response) ? response : []).map(report => ({
        id: report.id,
        project_name: report.project_name,
        project_location: report.project_location,
        date: report.date,
        status: report.status,
        formStep: report.status === 'processing' ? report.form_step : null
      }));
    } else {
      reports.value = [];
    }
  };

  const exportReportAsPdf = async (reportId) => {
    try {
      const response = await ReportsAPI.exportReport(reportId);

      // Create a download link - response.data is already a blob
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from Content-Disposition header if available
      const filename =
        filenameFromContentDisposition(response.headers['content-disposition']) ||
        `report_${reportId}_${Date.now()}.pdf`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      exportSuccess.value = true;
      showToast('Report has been downloaded successfully!', {
        type: 'success',
      });

      return true;
    } catch (error) {
      showToast('Failed to generate PDF. Please try again.', {
        type: 'error',
      });
      console.error('PDF export error:', error);
      return false;
    }
  };

  const addEmailRecipient = () => {
    emails.value.push({ email: '', id: Date.now() });
  };

  const removeEmailRecipient = (id) => {
    emails.value = emails.value.filter(item => item.id !== id);
  };

  const initializeEmails = (userEmail) => {
    if (emails.value.length === 0 && userEmail) {
      emails.value = [{ email: userEmail, id: Date.now() }];
    }
  };

  const validateEmails = () => {
    const validEmails = emails.value.filter(item =>
      item.email && item.email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(item.email.trim())
    );

    if (validEmails.length === 0) {
      showToast('Please add at least one valid email address.', { type: 'error' });
      return false;
    }

    return true;
  };

  const sendReportByEmail = async (reportId, contactMe) => {
    if (!validateEmails()) {
      return false;
    }

    try {
      const validEmails = emails.value
        .filter(item => item.email && item.email.trim())
        .map(item => item.email.trim());

      // Send to each email individually for proper tracking
      const promises = validEmails.map(emailAddress =>
        ReportsAPI.emailReport(reportId, emailAddress, contactMe, contactMe)
      );

      await Promise.all(promises);

      exportSuccess.value = true;
      showToast(`Report email queued successfully to ${validEmails.length} recipient(s)!`, {
        type: 'success',
      });
      return true;
    } catch (error) {
      showToast('Failed to send email. Please try again.', {
        type: 'error',
      });
      console.error('Email send error:', error);
      return false;
    }
  };

  const handleExport = async (type, reportId) => {
    if (type === 'pdf') {
      const success = await exportReportAsPdf(reportId);
      if (success) {
        setTimeout(() => {
          exportSuccess.value = false;
        }, 2000);
      }
      return success;
    } else if (type === 'email') {
      return await sendReportByEmail(reportId, contactMe.value);
    }
    return false;
  };

  return {
    reports,
    fetchReports,
    // Export functionality
    emails,
    contactMe,
    exportSuccess,
    handleExport,
    exportReportAsPdf,
    sendReportByEmail,
    addEmailRecipient,
    removeEmailRecipient,
    initializeEmails,
    validateEmails
  };
}
